import React, { useState, useEffect } from "react";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getMe } from "../features/authSlice";
import { MdKeyboardArrowDown, MdMenu, MdClose, MdSettings } from "react-icons/md";
import { FaLeaf } from "react-icons/fa";
import avatar from "../assets/avatar.jpg";
import { UserProfile } from ".";
import { useStateContext } from "../contexts/ContextProvider";

const NavButton = ({ title, customFunc, icon }) => {
  const { currentMode } = useStateContext();
  const isDark = currentMode === 'Dark';

  return (
    <button
      type="button"
      onClick={() => customFunc()}
      className={`text-xl rounded-lg p-2.5 transition-all duration-300 ${
        isDark
          ? 'text-gray-300 hover:bg-gray-700'
          : 'text-gray-700 hover:bg-gray-100'
      }`}
      title={title}
    >
      {icon}
    </button>
  );
};

const Navbar = () => {
  const [urlImage, setUrlImage] = useState(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);

  const {
    activeMenu,
    setActiveMenu,
    handleClick,
    isClicked,
    setScreenSize,
    screenSize,
    currentMode,
    setThemeSettings,
  } = useStateContext();

  // Colors based on theme
  const isDark = currentMode === 'Dark';
  const accentColor = '#c9862a';
  const bgColor = isDark ? '#282C33' : '#ffffff';
  const borderColor = isDark ? '#3a3a34' : '#e8e0d0';
  const textPrimary = isDark ? '#f0ece3' : '#1a1a18';
  const textSecondary = isDark ? '#a09880' : '#6b6456';

  useEffect(() => {
    dispatch(getMe());
  }, [dispatch]);

  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    if (token) {
      if (user && user.role === "guru") {
        getProfileGuru();
      } else if (user && user.role === "siswa") {
        getProfileSiswa();
      }
    } else {
      navigate("/login");
    }
  }, [navigate, user]);

  const getProfileSiswa = async () => {
    try {
      const token = localStorage.getItem("accessToken");
      const apiUrl = process.env.REACT_APP_URL_API;
      const response = await axios.get(`${apiUrl}/profile-siswa`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.data && response.data.data) {
        const profileData = response.data.data;
        setUrlImage(profileData.url);
      }
    } catch (error) {
      console.error("Error mengambil profile siswa:", error);
    }
  };

  const getProfileGuru = async () => {
    try {
      const token = localStorage.getItem("accessToken");
      const apiUrl = process.env.REACT_APP_URL_API;
      const response = await axios.get(`${apiUrl}/profile-guru`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.data && response.data.data) {
        const profileData = response.data.data;
        setUrlImage(profileData.url);
      }
    } catch (error) {
      console.error("Error mengambil profile guru:", error);
    }
  };

  const capitalizeFirstLetter = (string) => {
    if (!string) return "";
    return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
  };

  useEffect(() => {
    const handleResize = () => setScreenSize(window.innerWidth);
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (screenSize <= 900) {
      setActiveMenu(false);
    } else {
      setActiveMenu(true);
    }
  }, [screenSize]);

  const handleActiveMenu = () => setActiveMenu(!activeMenu);

  return (
    <div
      className="flex justify-between items-center p-3 relative z-50 border-b"
      style={{
        backgroundColor: bgColor,
        borderColor: borderColor,
        borderBottomWidth: '2px',
      }}
    >
      {/* Left Section - Menu Button */}
      <NavButton
        title="Menu"
        customFunc={handleActiveMenu}
        icon={activeMenu ? <MdClose /> : <MdMenu />}
      />

      {/* Center - Brand Logo (visible on mobile) */}
      <div
        className="flex items-center gap-2 md:hidden cursor-pointer"
        onClick={() => navigate("/dashboard")}
      >
        <div
          className="w-7 h-7 rounded flex items-center justify-center"
          style={{ backgroundColor: accentColor }}
        >
          <FaLeaf className="text-white text-xs" />
        </div>
        <div>
          <span
            className="font-serif text-sm font-bold"
            style={{ color: textPrimary }}
          >
            Rumoh Pangan{' '}
          </span>
          <span
            className="font-serif text-sm font-bold"
            style={{ color: accentColor }}
          >
            Aceh
          </span>
        </div>
      </div>

      {/* Right Section - Settings & User Profile */}
      <div className="flex items-center gap-2">
        {/* Theme Settings Button */}
        <NavButton
          title="Settings"
          customFunc={() => setThemeSettings(true)}
          icon={<MdSettings />}
        />

        {/* User Profile Dropdown */}
        <div
          className={`flex items-center gap-2.5 cursor-pointer p-2 rounded-lg transition-all duration-300 relative z-50 ${
            isDark
              ? 'hover:bg-gray-700'
              : 'hover:bg-gray-100'
          }`}
          onClick={() => handleClick("userProfile")}
        >
          <img
            className="rounded-full w-9 h-9 border-2 object-cover transition-all duration-300 hover:scale-105"
            style={{ borderColor: accentColor }}
            src={urlImage && urlImage !== "http://localhost:5000/images/null" ? urlImage : avatar}
            alt="user-profile"
          />

          <div className="hidden md:block">
            <p
              className={`text-sm font-semibold ${
                isDark ? 'text-gray-200' : 'text-gray-800'
              }`}
              style={{ color: textPrimary }}
            >
              {user ? capitalizeFirstLetter(user.username) : "Admin"}
            </p>
            <p
              className={`text-xs capitalize ${
                isDark ? 'text-gray-400' : 'text-gray-500'
              }`}
              style={{ color: textSecondary }}
            >
              {user?.role || 'Admin'}
            </p>
          </div>

          <MdKeyboardArrowDown
            className={`text-lg ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
          />
        </div>

        {/* User Profile Dropdown Menu */}
        {isClicked.userProfile && (
          <div className="absolute top-full right-0 mt-2 z-[9999]">
            <UserProfile />
          </div>
        )}
      </div>
    </div>
  );
};

export default Navbar;